// ═══════════════════════════════════════════════════════════════════════
// TryOnService.js — Essayage virtuel de tenues sur mannequin IA
// ═══════════════════════════════════════════════════════════════════════
//
// v1.10.0 : Nouvelle feature backed by FASHN.ai
// v1.10.1 : Ajout de 4 mannequins européens supplémentaires (f6/f7/m6/m7)
// v1.12.6 : Sélection du provider VTO (FASHN ou Google Vertex) côté UI
//           pour A/B test utilisateur. Le provider voyage avec le payload
//           et override la variable d'env TRYON_PROVIDER côté backend.
//
// Workflow :
//   1. User clique "Essayer" sur un outfit dans Sélection styliste
//   2. Modal sélection mannequin (14 mannequins pré-générés)
//   3. Appel à /tryon avec mannequinId + URLs des pièces
//   4. Lambda : check cache S3 → si MISS, appels FASHN séquentiels
//   5. Affichage du résultat avec actions (sauver, partager, ré-essayer)
//
// Quotas par tier (defined in PremiumService.js TIERS) :
//   - Free : 4/mois (1/sem)
//   - Basic : 5/mois
//   - Premium : 15/mois
//   - Gold : 40/mois
//
// Cache : géré côté backend (S3). Frontend ne sait pas si HIT ou MISS.
// ═══════════════════════════════════════════════════════════════════════

import AsyncStorage from '@react-native-async-storage/async-storage';

const API_URL = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';

// v1.13.86 — fetch avec TIMEOUT (AbortController). Sans ça, un fetch RN qui
// pendouille (réseau instable pendant l'upload de la photo perso) laissait le
// try-on bloqué sur "uploading your photo" à l'infini alors que l'upload avait
// réussi côté serveur (photo retrouvée après réouverture).
async function _fetchTO(url, opts = {}, ms = 20000) {
  const ctl = new AbortController();
  const to = setTimeout(() => { try { ctl.abort(); } catch {} }, ms);
  try {
    return await fetch(url, { ...opts, signal: ctl.signal });
  } finally { clearTimeout(to); }
}

// ─── Mannequins pré-générés ───
//
//   Liste statique des mannequins disponibles. Les images sont générées une
//   fois via FASHN /v1/run model-create et stockées sur S3 public. Quand on
//   ajoute un mannequin, on met à jour cette liste + on ajoute son image
//   sur S3 (cf. aws/scripts/generate-mannequins.js).
//
//   Note : ces mannequins sont partagés entre tous les users → maximise le
//   cache hit rate (clé cache = mannequin_id + outfit_signature).
//
//   v1.10.0 : 10 mannequins (5 femmes, 5 hommes, diversité ethnique mondiale)
//   v1.10.1 : +4 européens (f6, f7, m6, m7) → 14 mannequins au total
const S3_BASE = 'https://outfit-tryon-cache.s3.eu-west-3.amazonaws.com/mannequins';

export const MANNEQUINS = [
  // Femmes — diversité mondiale
  { id: 'f1', label: 'Léa',     gender: 'F', ethnicity: 'caucasienne',  bodyType: 'mince',      image: `${S3_BASE}/f1.jpg` },
  { id: 'f2', label: 'Amira',   gender: 'F', ethnicity: 'maghrébine',   bodyType: 'mince',      image: `${S3_BASE}/f2.jpg` },
  { id: 'f3', label: 'Sofia',   gender: 'F', ethnicity: 'latine',       bodyType: 'curvy',      image: `${S3_BASE}/f3.jpg` },
  { id: 'f4', label: 'Mei',     gender: 'F', ethnicity: 'asiatique',    bodyType: 'mince',      image: `${S3_BASE}/f4.jpg` },
  { id: 'f5', label: 'Aïcha',   gender: 'F', ethnicity: 'africaine',    bodyType: 'athlétique', image: `${S3_BASE}/f5.jpg` },
  // Femmes européennes (v1.10.1)
  { id: 'f6', label: 'Emma',    gender: 'F', ethnicity: 'européenne',   bodyType: 'mince',      hair: 'blonde',    image: `${S3_BASE}/f6.jpg` },
  { id: 'f7', label: 'Chloé',   gender: 'F', ethnicity: 'européenne',   bodyType: 'curvy',      hair: 'brune',     image: `${S3_BASE}/f7.jpg` },
  // Hommes — diversité mondiale
  { id: 'm1', label: 'Lucas',   gender: 'M', ethnicity: 'caucasien',    bodyType: 'mince',      image: `${S3_BASE}/m1.jpg` },
  { id: 'm2', label: 'Karim',   gender: 'M', ethnicity: 'maghrébin',    bodyType: 'athlétique', image: `${S3_BASE}/m2.jpg` },
  { id: 'm3', label: 'Diego',   gender: 'M', ethnicity: 'latin',        bodyType: 'athlétique', image: `${S3_BASE}/m3.jpg` },
  { id: 'm4', label: 'Hiroshi', gender: 'M', ethnicity: 'asiatique',    bodyType: 'mince',      image: `${S3_BASE}/m4.jpg` },
  { id: 'm5', label: 'Mamadou', gender: 'M', ethnicity: 'africain',     bodyType: 'athlétique', image: `${S3_BASE}/m5.jpg` },
  // Hommes européens (v1.10.1)
  { id: 'm6', label: 'Thomas',  gender: 'M', ethnicity: 'européen',     bodyType: 'athlétique', hair: 'brune',     image: `${S3_BASE}/m6.jpg` },
  { id: 'm7', label: 'Hugo',    gender: 'M', ethnicity: 'européen',     bodyType: 'mince',      hair: 'blonde',    image: `${S3_BASE}/m7.jpg` },
];

export function getMannequinsByGender(gender) {
  if (!gender) return MANNEQUINS;
  return MANNEQUINS.filter(m => m.gender === gender);
}

export function getMannequinById(id) {
  return MANNEQUINS.find(m => m.id === id) || null;
}

// v1.12.175 : "Mon mannequin" — photo perso de l'user, utilisée comme
// personImage par Vertex VTO (silhouette/visage/taille = ressemblance max).
// Le backend résout l'id 'personal' en presigned URL S3 au moment du job.
export function buildPersonalMannequinEntry({ viewUrl } = {}) {
  return {
    id: 'personal',
    label: 'Mon mannequin',
    isPersonal: true,
    bodyType: 'votre silhouette',
    image: viewUrl || null,
  };
}

// ─── Personal mannequin API helpers (v1.12.175) ───────────────────
export async function fetchPersonalMannequin(authToken) {
  if (!authToken) throw new Error('authToken requis');
  const res = await _fetchTO(`${API_URL}/profile/personal-mannequin`, {
    headers: { Authorization: `Bearer ${authToken}` },
  }, 15000);
  if (!res.ok) throw new Error(`fetchPersonalMannequin ${res.status}`);
  return res.json();
}

export async function requestPersonalMannequinUpload({ authToken, contentType }) {
  if (!authToken) throw new Error('authToken requis');
  const res = await _fetchTO(`${API_URL}/profile/personal-mannequin/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ contentType: contentType || 'image/jpeg' }),
  }, 15000);
  if (res.status === 429) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || 'Vous pouvez modifier votre photo 1 fois par semaine.');
    err.code = 'PERSONAL_MANNEQUIN_RATE_LIMITED';
    err.retryAfterSec = data.retry_after_sec || null;
    err.nextChangeAt = data.next_change_at || null;
    throw err;
  }
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`upload-url ${res.status}: ${t.slice(0, 120)}`);
  }
  return res.json();
}

export async function uploadPersonalMannequinFile({ uploadUrl, fileUri, contentType }) {
  // Sur RN, fetch peut envoyer un Blob/Uri directement via {uri,name,type} (FormData)
  // mais pour PUT signé, on envoie le binaire brut. expo-image-picker renvoie une URI
  // file://… : on lit en blob.
  const blob = await (await fetch(fileUri)).blob();
  // PUT S3 : timeout plus généreux (upload d'image) mais borné → jamais infini.
  const res = await _fetchTO(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType || 'image/jpeg' },
    body: blob,
  }, 45000);
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`PUT S3 ${res.status}: ${t.slice(0, 120)}`);
  }
  return true;
}

export async function confirmPersonalMannequin({ authToken, s3Key }) {
  const res = await _fetchTO(`${API_URL}/profile/personal-mannequin/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ s3Key }),
  }, 15000);
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`confirm ${res.status}: ${t.slice(0, 120)}`);
  }
  return res.json();
}

export async function deletePersonalMannequin(authToken) {
  const res = await fetch(`${API_URL}/profile/personal-mannequin`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) throw new Error(`delete ${res.status}`);
  return res.json();
}

// ─── Providers VTO disponibles (v1.12.6) ───
//
// Le user peut choisir le moteur IA pour comparer les rendus avant qu'on
// décide lequel garder en prod. La sélection voyage avec le payload et
// override la variable d'env TRYON_PROVIDER côté backend.
//
// v1.12.84 — FASHN supprimé. Vertex est désormais le seul provider, utilisé
// automatiquement à la sélection du mannequin (plus de UI choice).
// 'vertex' : Google Vertex AI virtual-try-on-001 (~$0.04/appel)
export const TRYON_PROVIDERS = [
  { id: 'vertex', label: 'Google Vertex', shortLabel: 'Vertex' },
];

export const DEFAULT_PROVIDER = 'vertex';

export function getProviderById(id) {
  return TRYON_PROVIDERS.find(p => p.id === id) || TRYON_PROVIDERS[0];
}

// ─── Compteur Try-ons local (cache d'affichage UNIQUEMENT) ───
//
//   v1.10.18 : Le cache local n'est qu'un "preview" pour éviter le flash "..."
//   pendant que fetchTryOnUsage charge depuis la DB. Le backend reste la source
//   de vérité unique pour les quotas — il calcule la fenêtre selon le tier
//   (hebdo Free / current_period_start abonnés) et stocke chaque Try-on dans
//   la table RDS `tryon_usage`. Donc même après désinstall/reinstall, le
//   compteur reste exact car récupéré depuis RDS au prochain ouvrir-modal.
//
//   On NE FAIT PLUS de reset local (avant : reset si monthKey change).
//   Le backend détermine seul quand le compteur doit repartir à 0.
const STORAGE_KEY_USAGE = '@outfit:tryon_usage_cache';

export async function getLocalUsage() {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY_USAGE);
    if (!raw) return { count: 0 };
    const parsed = JSON.parse(raw);
    return { count: parsed.count || 0 };
  } catch {
    return { count: 0 };
  }
}

export async function setLocalUsage(count) {
  try {
    await AsyncStorage.setItem(STORAGE_KEY_USAGE, JSON.stringify({ count, at: Date.now() }));
  } catch (e) {
    console.warn('[TryOn] setLocalUsage failed:', e.message);
  }
}

export async function incrementLocalUsage() {
  const current = await getLocalUsage();
  const updated = { count: current.count + 1 };
  await setLocalUsage(updated.count);
  return updated;
}

// ═══ APPEL PRINCIPAL : essayer une tenue (PATTERN ASYNC v1.10.6) ═══
//
//   1. POST /tryon → reçoit jobId immédiatement (≤ 2s)
//   2. GET /tryon/job/:id → poll toutes les 2s jusqu'à completion
//   3. Retourne le résultat final (image URL)
//
//   onProgress(stage) — callback optionnel pour afficher l'avancement
//     stage : 'created' | 'processing' | 'completed' | 'failed'
//
// @param {string} mannequinId - ID du mannequin choisi (ex: 'f1')
// @param {string[]} garmentImages - URLs des pièces (max 4)
// @param {string} authToken - JWT Cognito de l'utilisateur
// @param {function} onProgress - callback de progression (optionnel)
// @param {string} provider - 'fashn' | 'vertex' (v1.12.6, optionnel — défaut backend)
// @returns {Promise<{ imageUrl, fromCache, usage, limit, provider }>}
export async function performTryOn({ mannequinId, garmentImages, authToken, onProgress, provider }) {
  if (!mannequinId) throw new Error('mannequinId requis');
  if (!Array.isArray(garmentImages) || garmentImages.length === 0) {
    throw new Error('Au moins 1 pièce à essayer');
  }
  if (!authToken) throw new Error('authToken requis');

  // v1.12.370 — GA4 event: tryon_started
  const tryonStart = Date.now();
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('tryon_started', {
      mannequin_id: mannequinId,
      garment_count: garmentImages.length,
      provider: provider || 'default',
      is_personal: mannequinId === 'personal',
    });
  } catch {}

  // ─── 1. Créer le job (POST /tryon) ───
  // v1.12.175 : si mannequinId='personal', le backend résout l'image
  // depuis la photo perso de l'user — pas besoin de mannequinImage côté front.
  const body = {
    mannequinId,
    garmentImages: garmentImages.slice(0, 4),
  };
  if (mannequinId !== 'personal') {
    const mannequin = getMannequinById(mannequinId);
    if (!mannequin) throw new Error(`Mannequin inconnu: ${mannequinId}`);
    body.mannequinImage = mannequin.image;
  }
  // v1.12.6 : provider override (passé au worker via le job payload)
  if (provider === 'fashn' || provider === 'vertex') {
    body.provider = provider;
  }

  // v1.13.87 — timeout : sans ça, un POST /tryon qui pendouille laissait le
  // try-on bloqué sur "preparing" à l'infini (jamais de jobId → jamais de poll).
  const response = await _fetchTO(`${API_URL}/tryon`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(body),
  }, 25000);

  if (response.status === 429) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(data.message || 'Quota de Try-ons atteint');
    err.code = 'QUOTA_EXCEEDED';
    err.usage = data.usage || null;
    err.limit = data.limit || null;
    err.tier = data.tier || null;
    err.nextResetDate = data.nextResetDate || null;
    err.isPaidTier = data.isPaidTier || false;
    throw err;
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`API tryon ${response.status}: ${text.slice(0, 200)}`);
  }

  const job = await response.json();
  if (!job.jobId) throw new Error('Pas de jobId retourné par l\'API');

  if (onProgress) onProgress('created');

  // ─── 2. Poll jusqu'à completion (max 90s) ───
  const finalResult = await pollTryOnJob({
    jobId: job.jobId,
    authToken,
    onProgress,
    maxWaitMs: 90000,
  });

  await incrementLocalUsage();

  // v1.12.370 — GA4 event: tryon_completed (succès)
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('tryon_completed', {
      mannequin_id: mannequinId,
      garment_count: garmentImages.length,
      provider: finalResult.provider || provider || 'default',
      from_cache: !!finalResult.fromCache,
      duration_ms: Date.now() - tryonStart,
      is_personal: mannequinId === 'personal',
    });
  } catch {}

  return {
    imageUrl: finalResult.imageUrl,
    fromCache: finalResult.fromCache || false,
    usage: (job.usage || 0) + 1,
    limit: job.limit || null,
    provider: finalResult.provider || provider || null,  // v1.12.6
  };
}

// ═══ Polling du statut d'un job ═══
//
// Strategie : poll toutes les 2s avec backoff léger (max 4s).
// Timeout total : 90s.
async function pollTryOnJob({ jobId, authToken, onProgress, maxWaitMs = 90000 }) {
  const start = Date.now();
  let delay = 2000;

  while (Date.now() - start < maxWaitMs) {
    await new Promise(r => setTimeout(r, delay));

    let res;
    try {
      // v1.13.87 — timeout PAR requête : sans ça, un GET de poll qui pendouille
      // bloquait la boucle (l'await ne rendait jamais la main → le garde 90s ne
      // se ré-évaluait jamais → "preparing" infini). Sur abort → catch → retry.
      res = await _fetchTO(`${API_URL}/tryon/job/${jobId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      }, 12000);
    } catch (e) {
      // Erreur réseau / timeout temporaire, on retry au prochain tour
      console.warn('[TryOn] poll network error:', e.message);
      delay = Math.min(4000, delay + 500);
      continue;
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Polling échec ${res.status}: ${text.slice(0, 150)}`);
    }

    const data = await res.json();
    if (onProgress) onProgress(data.status);

    if (data.status === 'completed') {
      if (!data.imageUrl) throw new Error('Job complété mais sans imageUrl');
      return data;
    }
    if (data.status === 'failed') {
      throw new Error(data.error || 'Génération échouée côté serveur');
    }

    // pending / processing → continue polling
    delay = Math.min(4000, delay + 200);
  }

  throw new Error(`Timeout après ${maxWaitMs / 1000}s — la génération prend plus longtemps que prévu, réessayez.`);
}

// v1.12.140 — performTryOnSharp supprime : try-on uniquement Vertex (mannequin Google)

// ═══ Récupérer l'usage du mois côté backend (source de vérité) ═══
//
// v1.10.18 : Cette fonction est appelée :
//   - À l'ouverture du modal Try-on (refresh du cache)
//   - Au login / app start (sync précoce pour les écrans qui affichent le quota)
//
// Le résultat est aussi stocké en cache local AsyncStorage pour affichage
// instantané avant le prochain fetch.
export async function fetchTryOnUsage(authToken) {
  if (!authToken) return { count: 0, limit: 0, tier: 'free', nextResetDate: null, isPaidTier: false };
  try {
    const response = await fetch(`${API_URL}/tryon/usage`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) return { count: 0, limit: 0, tier: 'free', nextResetDate: null, isPaidTier: false };
    const data = await response.json();
    // v1.10.18 : On synchronise le cache local avec la valeur backend
    if (typeof data.count === 'number') {
      await setLocalUsage(data.count);
    }
    return data;
  } catch (e) {
    console.warn('[TryOn] fetchTryOnUsage failed:', e.message);
    return { count: 0, limit: 0, tier: 'free', nextResetDate: null, isPaidTier: false };
  }
}
